import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../config';
import logo from '../assets/images/candid-i-logo.png';
import {
  clampText,
  formatMarketDate,
  handleMarketImageError,
  normalizeMarketIntelligenceItem,
} from './marketIntelligenceUtils';

const HUB_NAV = [
  { label: 'Intelligence', href: '/market-intelligence/archive' },
  { label: 'Markets', href: '/market-intelligence/subscribe' },
  { label: 'Briefings', href: '/market-intelligence/briefing' },
];

const normalizeCategories = (categories = []) => {
  const safeCategories = Array.isArray(categories) ? categories : [];
  return [
    { key: 'all', label: 'All intelligence', icon: 'All' },
    ...safeCategories.map((category) => ({
      key: category.slug || category.name,
      label: category.name,
      icon: category.icon || 'Insight',
    })),
  ];
};

const MarketIntelligenceHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [taxonomy, setTaxonomy] = useState({ practices: [], functions: [] });
  const [selectedPractice, setSelectedPractice] = useState('');
  const [selectedVertical, setSelectedVertical] = useState('');
  const [selectedFunction, setSelectedFunction] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSent, setNewsletterSent] = useState(false);

  useEffect(() => {
    fetchHubData();
  }, []);

  const fetchHubData = async () => {
    try {
      setLoading(true);
      const [blogResponse, categoryResponse, taxonomyResponse] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/market-intelligence/articles`, { params: { page: 1, per_page: 24 } }),
        axios.get(`${API_BASE_URL}/api/categories`),
        axios.get(`${API_BASE_URL}/api/market-intelligence/taxonomy`),
      ]);

      const apiArticles = blogResponse.status === 'fulfilled' ? (blogResponse.value.data?.data || []) : [];
      const apiCategories = categoryResponse.status === 'fulfilled' ? (categoryResponse.value.data?.data || []) : [];
      const apiTaxonomy = taxonomyResponse.status === 'fulfilled'
        ? (taxonomyResponse.value.data?.data || { practices: [], functions: [] })
        : { practices: [], functions: [] };
      const normalizedArticles = apiArticles
        .map(normalizeMarketIntelligenceItem)
        .sort((a, b) => new Date(b.published_at) - new Date(a.published_at));

      setArticles(normalizedArticles);
      setCategories(normalizeCategories(apiCategories));
      setTaxonomy(apiTaxonomy);
    } catch (error) {
      console.error('Error loading market intelligence hub:', error);
      setArticles([]);
      setCategories(normalizeCategories([]));
    } finally {
      setLoading(false);
    }
  };

  const featuredArticle = useMemo(() => {
    const manualFeature = articles.find((article) => article.is_featured);
    return manualFeature || articles[0] || null;
  }, [articles]);

  const trustStats = useMemo(() => ([
    { label: 'Structured by ALICE', value: 'Signals gathered and organised systematically' },
    { label: 'Specialist coverage', value: 'Talent markets across priority sectors and regions' },
    { label: 'Human interpretation', value: 'Reviewed by experienced market specialists' },
    { label: 'Decision-ready', value: 'Built for hiring, career and workforce decisions' },
  ]), []);

  const spotlightArticles = useMemo(() => {
    const ordered = articles.filter((article) => article.slug !== featuredArticle?.slug);
    return [featuredArticle, ...ordered].filter(Boolean).slice(0, 3);
  }, [articles, featuredArticle]);

  const spotlightLead = spotlightArticles[0] || null;
  const spotlightSupport = spotlightArticles.slice(1, 3);

  const videoBriefs = useMemo(() => {
    return articles.filter((article) => {
      const hasVideo = Boolean(article.video_url || article.media_type === 'video');
      const isPremiumBrief = ['briefing', 'report'].includes(article.content_type);
      return hasVideo || isPremiumBrief;
    }).slice(0, 3);
  }, [articles]);

  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      const matchesSearch =
        searchQuery.trim() === '' ||
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
        article.topics.some((topic) => topic.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory =
        selectedCategory === 'all' ||
        article.category === selectedCategory ||
        article.raw?.category === selectedCategory;
      const matchesPractice =
        !selectedPractice || article.raw?.practice_id === selectedPractice;
      const matchesVertical =
        !selectedVertical || (article.raw?.vertical_ids || []).includes(selectedVertical);
      const matchesFunction =
        !selectedFunction || (article.raw?.function_ids || []).includes(selectedFunction);

      return matchesSearch && matchesCategory && matchesPractice && matchesVertical && matchesFunction;
    });
  }, [articles, searchQuery, selectedCategory, selectedPractice, selectedVertical, selectedFunction]);

  const availableVerticals = useMemo(() => (
    taxonomy.practices.find((practice) => practice.id === selectedPractice)?.verticals || []
  ), [taxonomy, selectedPractice]);

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();
    setNewsletterSent(true);
    setNewsletterEmail('');
  };

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-[#0f172a]">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[88rem] items-center justify-between px-5 py-4 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-3">
            <img src={logo} alt="Candid-I" className="h-8 w-auto" />
          </button>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            {HUB_NAV.map((item) => (
              <Link key={item.href} to={item.href} className="transition hover:text-[#f7911d]">
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center">
            <Link
              to="/market-intelligence/briefing"
              className="rounded-full bg-[#0f172a] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#111c35]"
            >
              Join the market briefing
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section
          aria-labelledby="intelligence-hub-title"
          className="bg-[radial-gradient(circle_at_top_left,_#fff7eb_0%,_#f7f7f4_45%,_#eef2f7_100%)]"
        >
          <div className="mx-auto grid max-w-[88rem] gap-10 px-5 py-16 lg:grid-cols-[1.18fr_0.82fr] lg:px-8 lg:py-20">
            <div className="max-w-[620px]">
              <span className="inline-flex rounded-full border border-[#f7911d]/30 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d] shadow-sm">
                Market Intelligence Hub
              </span>
              <h1
                id="intelligence-hub-title"
                className="mt-6 text-4xl font-semibold tracking-tight text-slate-950 md:text-6xl"
              >
                See where talent is moving—
                <br className="hidden md:block" />
                {' '}and what it means.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 md:text-xl">
                Candid-I tracks talent movement, hiring demand, compensation and competitor activity across
                specialist markets. ALICE structures the signals. Our specialists interpret what matters.
              </p>
              <div className="mt-8 flex flex-col gap-3 min-[480px]:flex-row min-[480px]:flex-wrap">
                <Link
                  to="/market-intelligence/archive"
                  className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200/60 transition hover:translate-y-[-1px] hover:bg-[#e77e08]"
                >
                  Browse intelligence
                </Link>
                <Link
                  to="/market-intelligence/briefing"
                  className="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition hover:border-[#f7911d] hover:text-[#f7911d]"
                >
                  Join the market briefing
                </Link>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {[
                  { label: 'Market signals', value: 'Talent supply, demand and movement' },
                  { label: 'Decision context', value: 'Compensation, competitors and capability gaps' },
                  { label: 'Specialist markets', value: 'Commodities, financial markets and technology' },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f7911d]">
                      {item.label}
                    </div>
                    <div className="mt-2 text-sm text-slate-600">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 -z-10 rounded-[2rem] bg-gradient-to-br from-[#f7911d]/20 via-white to-slate-100 blur-3xl" />
              <div className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
                <div className="border-b border-slate-100 px-6 py-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                    Featured report
                  </div>
                  <h2 className="mt-2 text-2xl font-semibold text-slate-950">
                    {featuredArticle?.title || 'Market intelligence coming soon'}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {featuredArticle?.subtitle || featuredArticle?.excerpt || 'Decision-ready market intelligence from Candid-I.'}
                  </p>
                </div>

                {featuredArticle?.featured_image ? (
                    <img
                      src={featuredArticle.featured_image}
                      alt={featuredArticle.featured_image_alt || featuredArticle.title}
                      className="h-64 w-full object-cover"
                      onError={handleMarketImageError}
                    />
                ) : (
                  <div className="flex h-64 items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 px-8 text-center text-white">
                    <div>
                      <div className="text-xs uppercase tracking-[0.3em] text-[#f7911d]">ALICE Intelligence</div>
                      <div className="mt-4 text-2xl font-semibold">
                        Structured signal, specialist context, decision-ready insight.
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid gap-3 px-6 py-6 sm:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                      Sector
                    </div>
                    <div className="mt-2 font-medium text-slate-900">
                      {featuredArticle?.sector?.join(', ') || featuredArticle?.category || 'Market intelligence'}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                      Published
                    </div>
                    <div className="mt-2 font-medium text-slate-900">
                      {formatMarketDate(featuredArticle?.published_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 pt-4 lg:px-8">
          <div className="grid gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 xl:grid-cols-4">
            {trustStats.map((item) => (
              <div key={item.label} className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                  {item.label}
                </div>
                <div className="mt-2 text-sm font-medium text-slate-700">{item.value}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-12 lg:px-8">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Spotlight research
              </div>
              <h2 className="mt-3 text-3xl font-semibold text-slate-950">Featured market intelligence</h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Selected analysis of talent movement, compensation and hiring activity across Candid-I's
                specialist markets.
              </p>
            </div>
            <Link to="/market-intelligence/archive" className="text-sm font-semibold text-[#f7911d]">
              View all intelligence
            </Link>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="grid gap-0 lg:grid-cols-[0.45fr_0.55fr]">
                <div className="min-h-[300px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
                  {spotlightLead?.featured_image ? (
                      <img
                        src={spotlightLead.featured_image}
                        alt={spotlightLead.featured_image_alt || spotlightLead.title}
                        className="h-full w-full object-cover"
                        onError={handleMarketImageError}
                      />
                  ) : (
                    <div className="flex h-full items-end p-6 text-white">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                          {spotlightLead?.category || 'Spotlight'}
                        </div>
                        <div className="mt-3 text-3xl font-semibold leading-tight">
                          {clampText(spotlightLead?.title || 'Spotlight research', 60)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col justify-between p-6 lg:p-8">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                      Featured intelligence
                    </div>
                    <h3 className="mt-3 text-3xl font-semibold leading-tight text-slate-950">
                      {spotlightLead?.title || 'Market intelligence coming soon'}
                    </h3>
                    <p className="mt-4 text-sm leading-7 text-slate-600">
                      {spotlightLead?.hero_supporting_line || spotlightLead?.subtitle || spotlightLead?.excerpt}
                    </p>
                    <div className="mt-6 rounded-2xl bg-slate-50 p-4">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">
                        Analyst view
                      </div>
                      <p className="mt-2 text-sm leading-7 text-slate-700">
                        {spotlightLead?.content_blocks?.[0]?.body
                          || 'This analysis highlights the market signal that matters most right now.'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-medium text-slate-500">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                      {spotlightLead?.content_type || 'report'}
                    </span>
                    <span>{formatMarketDate(spotlightLead?.published_at)}</span>
                    <span>{spotlightLead?.read_time_minutes} min read</span>
                    {spotlightLead?.video_duration_seconds ? (
                      <span className="rounded-full bg-orange-50 px-3 py-1 text-[#f7911d]">
                        {spotlightLead.video_duration_seconds}s clip
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-6">
                    <Link
                      to={`/market-intelligence/${spotlightLead?.slug || ''}`}
                      className="inline-flex rounded-full bg-[#f7911d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#e77e08]"
                    >
                        Read market outlook
                    </Link>
                  </div>
                </div>
              </div>
            </article>

            <div className="grid gap-5">
              {spotlightSupport.map((article) => (
                <article
                  key={article.id}
                  className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="grid gap-4 p-5 md:grid-cols-[0.34fr_0.66fr]">
                    <div className="aspect-[4/3] overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.featured_image_alt || article.title}
                          className="h-full w-full object-cover"
                          onError={handleMarketImageError}
                        />
                      ) : null}
                    </div>
                    <div className="flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                          {article.category}
                        </div>
                        <h3 className="mt-3 text-xl font-semibold text-slate-950">
                          <Link to={`/market-intelligence/${article.slug}`} className="transition hover:text-[#f7911d]">
                            {clampText(article.title, 60)}
                          </Link>
                        </h3>
                        <p className="mt-3 text-sm leading-7 text-slate-600">
                          {clampText(article.excerpt || article.subtitle || '', 180)}
                        </p>
                      </div>
                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                        <span className="rounded-full bg-slate-100 px-3 py-1">{article.content_type}</span>
                        <span>{formatMarketDate(article.published_at)}</span>
                        <span>{article.read_time_minutes} min</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-12 lg:px-8">
          <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Explore the intelligence
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Filter by market, sector, region, signal or research format.
              </div>
            </div>
            <div className="flex flex-1 gap-3 overflow-x-auto pb-1 lg:flex-[1.5]">
              {categories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => setSelectedCategory(category.key)}
                  aria-pressed={selectedCategory === category.key}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
                    selectedCategory === category.key
                      ? 'border-[#f7911d] bg-[#f7911d] text-white shadow-lg shadow-orange-200/60'
                      : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-[#f7911d] hover:text-[#f7911d]'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4 rounded-[2rem] bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
            <label htmlFor="market-intelligence-search" className="sr-only">
              Search market intelligence
            </label>
            <input
              id="market-intelligence-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search markets, sectors, roles and talent signals..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-[#f7911d] focus-visible:ring-2 focus-visible:ring-[#f7911d]/40"
            />
            <Link
              to="/market-intelligence/search"
              className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-[#f7911d] hover:text-[#f7911d]"
            >
              Advanced search
            </Link>
          </div>
          <div className="mt-4 grid gap-3 rounded-[2rem] border border-slate-200 bg-white p-4 md:grid-cols-3">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Practice
              <select
                value={selectedPractice}
                onChange={(event) => {
                  setSelectedPractice(event.target.value);
                  setSelectedVertical('');
                }}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-normal normal-case tracking-normal text-slate-800"
              >
                <option value="">All practices</option>
                {(taxonomy.practices || []).map((practice) => (
                  <option key={practice.id} value={practice.id}>{practice.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Vertical
              <select
                value={selectedVertical}
                disabled={!selectedPractice}
                onChange={(event) => setSelectedVertical(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-normal normal-case tracking-normal text-slate-800 disabled:opacity-50"
              >
                <option value="">All verticals</option>
                {availableVerticals.map((vertical) => (
                  <option key={vertical.id} value={vertical.id}>{vertical.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Function
              <select
                value={selectedFunction}
                onChange={(event) => setSelectedFunction(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-normal normal-case tracking-normal text-slate-800"
              >
                <option value="">All functions</option>
                {(taxonomy.functions || []).map((group) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-2 lg:px-8">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Latest intelligence
              </div>
              <h2 className="mt-3 text-3xl font-semibold text-slate-950">New signals and market analysis</h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Recent analysis across talent movement, hiring activity, compensation and specialist
                capability demand.
              </p>
            </div>
            <Link to="/market-intelligence/archive" className="text-sm font-semibold text-[#f7911d]">
              View all intelligence
            </Link>
          </div>

          {loading ? (
            <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-80 animate-pulse rounded-[2rem] bg-white shadow-sm" />
              ))}
            </div>
          ) : (
            <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredArticles.map((article) => (
                <article
                  key={article.id}
                  className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="aspect-[16/10] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
                    {article.featured_image ? (
                    <img
                        src={article.featured_image}
                        alt={article.featured_image_alt || article.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        onError={handleMarketImageError}
                      />
                    ) : (
                      <div className="flex h-full items-end p-6 text-white">
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                            {article.category}
                          </div>
                          <h3 className="text-2xl font-semibold leading-tight">{clampText(article.title, 60)}</h3>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 p-6">
                    <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                        {article.content_type}
                      </span>
                      <span>{formatMarketDate(article.published_at)}</span>
                      <span>{article.read_time_minutes} min read</span>
                    </div>

                    <h3 className="text-xl font-semibold leading-snug text-slate-950">
                      <Link to={`/market-intelligence/${article.slug}`} className="transition hover:text-[#f7911d]">
                        {article.title}
                      </Link>
                    </h3>
                    <p className="text-sm leading-7 text-slate-600">
                      {article.excerpt || article.subtitle}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {(article.topics || []).slice(0, 2).map((topic) => (
                        <span key={topic} className="rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-[#f7911d]">
                          {topic}
                        </span>
                      ))}
                      {(article.region || []).slice(0, 1).map((region) => (
                        <span key={region} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          {region}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                      <div className="text-sm text-slate-500">
                        By <span className="font-semibold text-slate-900">{article.author_name}</span>
                      </div>
                      <Link to={`/market-intelligence/${article.slug}`} className="text-sm font-semibold text-[#f7911d]">
                        Read more
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {videoBriefs.length > 0 && (
          <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-[2rem] bg-slate-950 p-8 text-white shadow-xl">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                  Video briefing
                </div>
                <h2 className="mt-4 text-3xl font-semibold">
                  Short premium clips for a faster read on the market.
                </h2>
                <p className="mt-4 text-sm leading-7 text-slate-300">
                  Video content should stay tight and executive-friendly. We recommend 90 to 120 seconds per clip.
                </p>
                <Link
                  to="/market-intelligence/briefing"
                  className="mt-6 inline-flex rounded-full bg-[#f7911d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#e77e08]"
                >
                  Join the market briefing
                </Link>
              </div>

              <div className="grid gap-5">
                {videoBriefs.map((article) => (
                  <div
                    key={article.id}
                    className="grid gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_auto]"
                  >
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                        {article.content_type}
                      </div>
                      <h3 className="mt-2 text-xl font-semibold text-slate-950">{article.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {article.video_caption || article.subtitle || article.excerpt}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-slate-500">
                        {article.video_duration_seconds ? (
                          <span className="rounded-full bg-orange-50 px-3 py-1 text-[#f7911d]">
                            {article.video_duration_seconds}s clip
                          </span>
                        ) : null}
                        {article.region?.[0] ? (
                          <span className="rounded-full bg-slate-100 px-3 py-1">{article.region[0]}</span>
                        ) : null}
                        {article.sector?.[0] ? (
                          <span className="rounded-full bg-slate-100 px-3 py-1">{article.sector[0]}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center justify-start md:justify-end">
                      <Link
                        to={`/market-intelligence/${article.slug}`}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#f7911d] hover:text-[#f7911d]"
                      >
                        Open
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="mx-auto max-w-[88rem] px-5 py-8 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Methodology
              </div>
              <h2 className="mt-3 text-3xl font-semibold text-slate-950">Built on market signal, not generic content.</h2>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                We combine internal search activity, professional conversations, market observations, and human specialist
                review to turn market activity into decision-ready intelligence.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  'Internal search activity',
                  'Client market observations',
                  'Professional conversations',
                  'Human specialist review',
                ].map((item) => (
                  <div key={item} className="rounded-2xl bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-[#0f172a] p-8 text-white shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Newsletter
              </div>
              <h2 className="mt-3 text-3xl font-semibold">Stay close to the market.</h2>
              <p className="mt-4 text-sm leading-7 text-slate-300">
                Get new intelligence notes, hiring signals, and market updates delivered straight to your inbox.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="mt-6 space-y-3">
                <label htmlFor="market-intelligence-email" className="sr-only">
                  Email address for market intelligence updates
                </label>
                <input
                  id="market-intelligence-email"
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-400 focus:border-[#f7911d]"
                />
                <button
                  type="submit"
                  className="w-full rounded-2xl bg-[#f7911d] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#e77e08]"
                >
                  Receive updates
                </button>
              </form>
              {newsletterSent && (
                <p className="mt-4 text-sm text-emerald-300">
                  Thanks, you are on the list.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-10 lg:px-8">
          <div className="rounded-[2rem] bg-[linear-gradient(135deg,_#f7911d_0%,_#f6b45e_100%)] px-8 py-10 text-white shadow-xl">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
              Final CTA
            </div>
            <h2 className="mt-3 text-3xl font-semibold md:text-4xl">
              Need a tailored read on a market, firm, or talent segment?
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-white/90">
              Use the hub to explore, then move into a private briefing or a candidate-led search.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/market-intelligence/briefing"
                className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0f172a] transition hover:bg-slate-50"
              >
                Join the market briefing
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default MarketIntelligenceHub;
